/*
 * The native shells' bridges, installed before any page mounts.
 *
 * Shared by the dashboard (app.js) and a customer's public website
 * (site.js): the report app opens /hub, /t, /n and /site pages in its
 * WebView, and whichever page is on screen when a universal link
 * arrives or a map asks for a position is the one that has to answer.
 * Does nothing outside a Capacitor shell.
 *
 * Geolocation polyfill for Capacitor WKWebView. iOS WKWebView's
 * navigator.geolocation often errors out (red-stripe state on the
 * maplibre GeolocateControl) even after the user grants permission,
 * because Capacitor's bridge doesn't always wire WKUIDelegate's
 * geolocation callbacks. The @capacitor/geolocation plugin uses
 * CoreLocation directly, so we route the standard Web Geolocation API
 * through it whenever we detect a native shell. Plain browsers / PWAs
 * fall through to the OS implementation untouched.
 */
// Set by installNativeShell once the polyfill is in; a no-op in a
// browser, where the OS owns the receiver and nothing here can help.
let _mapLocationInUseSetter = null;

/**
 * Tell the native shell whether the map's own location dot is being
 * looked at. False lets a watch opened with `tiqDemotable: true` stop
 * holding the receiver at full accuracy — see the demand notes in the
 * polyfill below. Any high-accuracy watch that is NOT demotable (a
 * recording) and any one-shot fix still get full accuracy.
 */
export function setMapLocationInUse(inUse) {
    _mapLocationInUseSetter?.(inUse);
}

// The field app reaches the function above through this event rather
// than an import: importing this module from a page splits it out of
// the entry chunk into a modulepreload of its own, and the field app's
// page has a byte budget (assetweight_verify). The listener is added
// at module load, so it is there in a browser too, where it is a no-op.
export const MAP_LOCATION_IN_USE_EVENT = 'tiq:map-location-in-use';
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener(MAP_LOCATION_IN_USE_EVENT, (e) => setMapLocationInUse(e.detail !== false));
}

export function installNativeShell() {
    const cap = typeof window !== 'undefined' ? window.Capacitor : null;
    if (!cap?.isNativePlatform?.()) return;

    // Show iOS's input accessory bar (Prev / Next / Done) above the
    // keyboard for every page in the WebView. Capacitor 8 defaults
    // hideKeyboardAccessoryBar to true; calling setAccessoryBarVisible
    // here — at script load, before any Vue page mounts — flips the
    // WKContentView swizzle in time for the very first focus event.
    // Doing this in a per-page onMounted races the first tap on a cold
    // launch: setAccessoryBarVisible is async (dispatch_async on the
    // main queue), so iOS builds the keyboard with the old hidden
    // state and only the second focus picks up the new one.
    const Keyboard = cap.Plugins?.Keyboard;
    if (Keyboard?.setAccessoryBarVisible) {
        Keyboard.setAccessoryBarVisible({ isVisible: true }).catch(() => {});
    }

    // Universal Link / App Link warm-launch handler. When the user
    // taps an app.trailsiq.com URL while the app is already running,
    // Capacitor's App plugin fires `appUrlOpen` with the full URL.
    // We navigate the WebView to that path so the matching Inertia
    // page renders.
    //
    // Field-app /field-app/* paths are EXCLUDED — FieldApp/App.vue
    // has its own listener that turns the deep link into a state
    // change (opening the issue / project overlay) without a full
    // reload. Doing both here would cause a redundant page swap.
    // Every other matched path (the report app's /hub, /t, /n,
    // /site surfaces) just gets a navigation hand-off.
    // @capacitor/app's addListener returns a PluginListenerHandle
    // synchronously on iOS (not a Promise), so chaining .catch() on
    // it throws "catch is not a function" — and because this whole
    // block runs at script top level, that throw aborts the rest of
    // app.js BEFORE createInertiaApp runs. Result: every fresh
    // install of the iOS shell hangs at the bundled splash and the
    // remote /login page lands with an empty <div id="app">. Wrap
    // the call in try/catch instead of relying on a return-value
    // shape we don't fully control across Capacitor versions.
    const AppPlugin = cap.Plugins?.App;
    if (AppPlugin && typeof AppPlugin.addListener === 'function') {
        try {
            AppPlugin.addListener('appUrlOpen', (event) => {
                try {
                    const url = event?.url;
                    if (!url) return;
                    const u = new URL(url);
                    if (u.host !== 'app.trailsiq.com') return;
                    if (u.pathname === '/field-app'
                        || u.pathname.startsWith('/field-app/')) {
                        return;
                    }
                    // Shared maintenance-task links land at the
                    // dashboard show route in the browser; in the
                    // app, FieldApp/App.vue's own appUrlOpen listener
                    // translates them into the issue-overlay action.
                    // Don't navigate the WebView away from /field-app
                    // to the dashboard show page.
                    if (/^\/maintenance\/\d+$/.test(u.pathname)) {
                        return;
                    }
                    const target = u.pathname + u.search + u.hash;
                    const current = window.location.pathname
                        + window.location.search
                        + window.location.hash;
                    if (target === current) return;
                    window.location.assign(url);
                } catch { /* malformed URL — ignore */ }
            });
        } catch { /* plugin return shape varies — fail open */ }
    }

    const Geolocation = cap.Plugins?.Geolocation;
    if (!Geolocation) return;
    // TrailsLocation is the field-app's custom Capacitor plugin
    // (TrailsLocationPlugin.swift). It owns its own long-lived
    // CLLocationManager and a cached last-known fix, so we can hand
    // maplibre's GeolocateControl a position synchronously instead of
    // waiting for CoreLocation's first fresh delegate fire — which on
    // cold-launch offline can stretch into minutes (no Assisted-GPS
    // ephemeris). It also swallows transient CoreLocation errors at
    // the native layer, so the watch never flaps maplibre into the
    // orange background-error state. Optional — when absent (report-
    // app, web), the polyfill falls back to @capacitor/geolocation.
    const TrailsLocation = cap.Plugins?.TrailsLocation;
    const watches = new Map();
    let nextWatchId = 1;

    // ── How much GPS the page actually needs ────────────────────────
    //
    // The plugin's location manager is started at plugin load, at its
    // best accuracy once a second, and until shells that answer
    // `setMode` nothing ever turned it down: `clearWatch` only removed
    // a JS listener and `enableHighAccuracy` was ignored. So every
    // battery measure written against the Web API — a low-accuracy
    // watch, a watch dropped while the phone is pocketed, a warm-up
    // that gives up — detached a listener and left the receiver
    // running exactly as before.
    //
    // The demand is now derived from the live watches, never set by a
    // caller: a caller cannot forget to release what it never claimed.
    //
    //   precise — a high-accuracy watch, or a one-shot fix in flight
    //   coarse  — only low-accuracy watches (auto-network)
    //   idle    — nothing, for IDLE_GRACE_MS, so a watch replaced by
    //             another a moment later does not cold-start the chip
    //
    // A watch marked `demotable` (the map's own location dot) counts as
    // coarse while the page says the map is out of use — see
    // setMapLocationInUse — and receives no fixes in that time, so the
    // dot holds its last precise position rather than being walked off
    // it by coarse ones.
    //
    // A shell without `setMode` rejects the call; that is swallowed and
    // the shell behaves exactly as it always has. The native side also
    // overrides all of this while a recording is active.
    const IDLE_GRACE_MS = 60_000;
    let pendingOneShots = 0;
    let mapInUse = true;
    let sentMode = null;
    let idleTimer = null;
    const demandedMode = () => {
        if (pendingOneShots > 0) return 'precise';
        let level = 0;
        for (const w of watches.values()) {
            if (w.kind !== 'trails') continue;
            const wants = w.high && (mapInUse || !w.demotable) ? 2 : 1;
            if (wants > level) level = wants;
        }
        return level === 2 ? 'precise' : (level === 1 ? 'coarse' : 'idle');
    };
    const sendMode = (mode) => {
        if (mode === sentMode) return;
        sentMode = mode;
        if (typeof TrailsLocation?.setMode !== 'function') return;
        try {
            Promise.resolve(TrailsLocation.setMode({ mode })).catch(() => {});
        } catch { /* older shell — the receiver stays as it was */ }
    };
    const syncMode = () => {
        if (!TrailsLocation) return;
        const mode = demandedMode();
        if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
        if (mode !== 'idle') { sendMode(mode); return; }
        idleTimer = setTimeout(() => {
            idleTimer = null;
            if (demandedMode() === 'idle') sendMode('idle');
        }, IDLE_GRACE_MS);
    };
    // Is this watch being fed right now? A demoted one is not.
    const watchReceives = (w) => !(w.high && w.demotable && !mapInUse);
    _mapLocationInUseSetter = (inUse) => {
        const next = inUse !== false;
        if (next === mapInUse) return;
        mapInUse = next;
        syncMode();
    };
    const fanOutPosition = (position) => ({
        coords: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude ?? null,
            altitudeAccuracy: position.coords.altitudeAccuracy ?? null,
            heading: position.coords.heading ?? null,
            speed: position.coords.speed ?? null,
        },
        timestamp: position.timestamp,
    });
    const fanOutError = (err) => ({
        code: err?.code ?? 2,
        message: err?.message || 'Geolocation error',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
    });
    const opts = (options) => ({
        enableHighAccuracy: !!options?.enableHighAccuracy,
        timeout: options?.timeout ?? 10000,
        maximumAge: options?.maximumAge ?? 0,
    });
    // Has the iOS / Android permission prompt been triggered yet this
    // process lifetime? TrailsLocationPlugin observes authorization
    // changes but doesn't itself request, so the polyfill must drive
    // the request through @capacitor/geolocation. Without this the
    // first getCurrentPosition call subscribes to locationUpdate
    // forever — CoreLocation never starts because permission stays
    // .notDetermined — and the gate sits on its watchdog spinner with
    // no prompt visible. (Apple App Review caught this as "loading
    // indefinitely on checking location" on iOS 26.3.)
    let permissionRequested = false;
    let permissionState = null; // 'granted' | 'denied' | null
    const ensurePermissionPrompt = () => {
        if (permissionRequested) return Promise.resolve(permissionState);
        permissionRequested = true;
        if (typeof Geolocation.requestPermissions !== 'function') {
            return Promise.resolve(null);
        }
        // Best-effort: if the user has already granted/denied this
        // resolves immediately with the current state. If it's
        // .notDetermined this surfaces the iOS system dialog.
        return Geolocation.requestPermissions({ permissions: ['location'] })
            .then((res) => {
                permissionState = res?.location ?? null;
                return permissionState;
            })
            .catch(() => null);
    };
    Object.defineProperty(navigator, 'geolocation', {
        configurable: true,
        value: {
            getCurrentPosition(success, error, options) {
                const maxAge = options?.maximumAge ?? 0;
                const timeoutMs = options?.timeout ?? 10000;
                if (TrailsLocation) {
                    // A one-shot fix is asked for because somebody wants
                    // to know where they are NOW, so it holds the
                    // receiver at full accuracy until it settles —
                    // whatever the watches would otherwise allow.
                    let oneShotDone = false;
                    pendingOneShots += 1;
                    syncMode();
                    const releaseOneShot = () => {
                        if (oneShotDone) return;
                        oneShotDone = true;
                        pendingOneShots = Math.max(0, pendingOneShots - 1);
                        syncMode();
                    };
                    const userSuccess = success;
                    const userError = error;
                    success = (p) => { releaseOneShot(); userSuccess?.(p); };
                    error = (e) => { releaseOneShot(); userError?.(e); };
                    // Cache-first: if the plugin already has a fresh
                    // enough fix (within maximumAge), return it
                    // synchronously. Otherwise fall through to a
                    // single-shot subscription on locationUpdate so we
                    // wait on OUR always-on CLLocationManager rather
                    // than spinning up @capacitor/geolocation's, which
                    // has no warm cache and would time out offline
                    // and bounce the report wizard's gate into the
                    // "Location permission needed" denied state.
                    TrailsLocation.getLastKnown().then((res) => {
                        if (res?.available && maxAge > 0) {
                            const age = Date.now() - (res.timestamp ?? 0);
                            if (age <= maxAge) {
                                success(fanOutPosition(res));
                                return;
                            }
                        }
                        let resolved = false;
                        let handlePromise = null;
                        let fallbackTimer = null;
                        const cleanupListener = () => {
                            Promise.resolve(handlePromise)
                                .then((h) => { try { h?.remove?.(); } catch {} })
                                .catch(() => {});
                        };
                        const timer = setTimeout(() => {
                            if (resolved) return;
                            resolved = true;
                            if (fallbackTimer) clearTimeout(fallbackTimer);
                            cleanupListener();
                            error?.(fanOutError({ code: 3, message: 'Geolocation timeout' }));
                        }, timeoutMs);
                        handlePromise = TrailsLocation.addListener('locationUpdate', (data) => {
                            if (resolved) return;
                            if (!data?.coords) return;
                            resolved = true;
                            clearTimeout(timer);
                            if (fallbackTimer) clearTimeout(fallbackTimer);
                            cleanupListener();
                            success(fanOutPosition(data));
                        });
                        // Stationary-fix fallback. CLLocationManager only
                        // re-fires didUpdateLocations when the device
                        // moves enough to clear the distance filter, so
                        // a forceRefresh call (maxAge=0) on a phone that
                        // hasn't moved — or a simulator pinned to a
                        // Custom Location — would never resolve via the
                        // listener and would always time out into the
                        // denied state. After a brief wait, accept
                        // whatever cached fix the plugin has; CL keeps
                        // its `.location` property current even when no
                        // delegate event fires.
                        fallbackTimer = setTimeout(() => {
                            if (resolved) return;
                            TrailsLocation.getLastKnown().then((latest) => {
                                if (resolved) return;
                                if (!latest?.available) return;
                                resolved = true;
                                clearTimeout(timer);
                                cleanupListener();
                                success(fanOutPosition(latest));
                            }).catch(() => {});
                        }, 1500);
                        // Surface the iOS prompt the first time JS
                        // asks for location. Once the user grants,
                        // TrailsLocationPlugin's authorization-change
                        // delegate fires startUpdatingLocation and the
                        // listener above resolves with the first fix.
                        // If the user denies, fail fast with code:1
                        // instead of making them stare at the spinner
                        // until the 12 s watchdog trips.
                        ensurePermissionPrompt().then((state) => {
                            if (resolved) return;
                            if (state === 'denied') {
                                resolved = true;
                                clearTimeout(timer);
                                if (fallbackTimer) clearTimeout(fallbackTimer);
                                cleanupListener();
                                error?.(fanOutError({ code: 1, message: 'Location permission denied' }));
                            }
                        });
                    }).catch(() => {
                        Geolocation.getCurrentPosition(opts(options))
                            .then((p) => success(fanOutPosition(p)))
                            .catch((err) => error?.(fanOutError(err)));
                    });
                    return;
                }
                Geolocation.getCurrentPosition(opts(options))
                    .then((p) => success(fanOutPosition(p)))
                    .catch((err) => error?.(fanOutError(err)));
            },
            watchPosition(success, error, options) {
                const synthId = nextWatchId++;
                if (TrailsLocation) {
                    // Emit cached fix synchronously so maplibre's
                    // GeolocateControl flips to active-lock immediately,
                    // then subscribe to the native event stream for
                    // live updates. No error path — the native plugin
                    // swallows transient CoreLocation failures.
                    const entry = {
                        kind: 'trails',
                        handle: null,
                        high: !!options?.enableHighAccuracy,
                        // Non-standard and ignored by every browser:
                        // marks a watch that may go quiet while the page
                        // says the map is out of use.
                        demotable: !!options?.tiqDemotable,
                    };
                    watches.set(synthId, entry);
                    syncMode();
                    TrailsLocation.getLastKnown()
                        .then((res) => { if (res?.available && watches.has(synthId)) success(fanOutPosition(res)); })
                        .catch(() => {});
                    entry.handle = TrailsLocation.addListener('locationUpdate', (data) => {
                        if (!data?.coords) return;
                        if (!watchReceives(entry)) return;
                        success(fanOutPosition(data));
                    });
                    // Same prompt-trigger as getCurrentPosition — without
                    // it, a watch starting before the user has granted
                    // location permission silently never fires.
                    ensurePermissionPrompt();
                    return synthId;
                }
                // Fallback: stock @capacitor/geolocation watch.
                // watchPosition can return either a Promise<CallbackID>
                // (older Capacitor) or a CallbackID directly (newer
                // Capacitor 8 builds); stash whichever and unwrap on
                // clear.
                const ready = Geolocation.watchPosition(opts(options), (p, err) => {
                    if (err) {
                        error?.(fanOutError(err));
                        return;
                    }
                    if (!p?.coords) return;
                    success(fanOutPosition(p));
                });
                watches.set(synthId, { kind: 'capacitor', ready });
                return synthId;
            },
            clearWatch(id) {
                const entry = watches.get(id);
                if (!entry) return;
                watches.delete(id);
                if (entry.kind === 'trails') {
                    Promise.resolve(entry.handle)
                        .then((h) => { try { h?.remove?.(); } catch {} })
                        .catch(() => {});
                    syncMode();
                    return;
                }
                const finalize = (cbid) => {
                    if (cbid == null) return;
                    try { Geolocation.clearWatch({ id: cbid }); } catch {}
                };
                const ready = entry.ready;
                if (ready && typeof ready.then === 'function') {
                    ready.then(finalize).catch(() => {});
                } else {
                    finalize(ready);
                }
            },
        },
    });
}
